// "Game Stats" after a win (legacy window/statsWin.js): count-up of moves, time, bonus, score and
// best score (one after another on desktop, ~0.7 s each; at once on mobile), the win POST with the
// anti-cheat hash and the shuffle pattern (logged-in players only count server side), share
// buttons that light up when the server answers ok, an ad slot, New game (full page reload to
// /#<game>: the reload refreshes the ads, owner decision) and Retry game (same deal).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type React from 'react';
import { env } from '../env';
import { mpStore, useMultiplayerState } from '../multiplayer/store';
import { fetchWaiting, waiterFor, type Waiter } from '../multiplayer/waiting';
import { WithFlag } from '../online/PlayerName';
import { reportWinLine } from '../ui/playReport';
import { winLineGroup } from '../ui/winLine';
import { winHash } from '../api/hashes';
import { post } from '../api/client';
import { winBonus } from '../engine/scoring';
import { backSrc, faceSrc, faceSrcSet } from '../options/assets';
import '../fonts/saloon.css'; // the saloon type of the headline and the numbers (Rye + accented letters, OFL, self-hosted)
import { t } from '../i18n';
import { uiStore, type WindowInstance } from '../store/uiStore';
import { AdSlot } from './AdSlot';
import { useFitWindow } from './useFitWindow';
import { adsEnabled, WindowFrame } from './WindowFrame';
import { onMediaChange } from '../ui/compat';

export interface WinData {
  game: string;
  scoring: 'standard' | 'timed';
  time: number;
  score: number;
  moves: number;
  prevDeal: boolean;
  pattern: number[];
  /** `/my-score/<hash>.html`: show someone's result, no count-up of the best score, no POST. */
  preview?: boolean;
  winnerName?: string | null;
  /** TEMPORARY test button (app-entry.tsx): the full look, but no best-score write and no save-win POST. */
  demo?: boolean;
}

/** Room for the second (top) ad slot (owner 2026-10-17: "jeśli urządzenie jest na tyle duże i jest miejsce
 *  na 2 sloty"): the window with both slots is about 800 px tall, plus the top bar and the bottom ad room. */
const TOP_AD_MEDIA = '(min-width: 760px) and (min-height: 940px)';

/** The fan behind the medal: the player's own royal flush of hearts (the board's card PNGs). */
const FAN = ['10h', 'Jh', 'Qh', 'Kh', 'Ah'];

/** The hero (owner pick 2026-10-17, prototype H): a fan of the player's real cards that opens when the
 *  window appears and sways in 3D, and a thick gold medal spinning in front of it (win.css .win_fan,
 *  .win_medal; edge layers give it its thickness). */
function WinHero({ deck }: { deck: string }) {
  return (
    <div className="win_trophy" aria-hidden="true">
      <div className="win_rays" />
      <div className="win_fan">
        {FAN.map((id, i) => (
          <img
            key={id}
            className="win_fan_card"
            src={faceSrc(deck, id)}
            srcSet={faceSrcSet(deck, id)}
            alt=""
            draggable={false}
            style={{ '--i': i - 2 } as React.CSSProperties}
          />
        ))}
      </div>
      <div className="win_medal">
        <div className="win_medal_spin">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="win_medal_edge" style={{ transform: `translateZ(${-i - 1}px)` }} />
          ))}
          <div className="win_medal_face">★</div>
          <div className="win_medal_face win_medal_back">♠</div>
        </div>
      </div>
    </div>
  );
}

/** The backdrop of the whole window body, behind the ads and the buttons too (owner 2026-10-17: "tym tłem
 *  wypełnij również sekcję reklamy oraz przycisków"): warm cream and a faint wallpaper of the player's card
 *  back. It is positioned against .lbx_content, so it stays still while the numbers scroll on a short
 *  screen. (The golden rays turn behind the hero, in WinHero.) */
export function WinBackdrop({ back }: { back: string }) {
  return (
    <div
      className="win_backdrop"
      aria-hidden="true"
      style={{ '--win-back': `url("${backSrc(back)}")` } as React.CSSProperties}
    />
  );
}

/** How long the confetti stays (the longest piece: delay 600 + fall 2000 ms). */
// owner 2026-10-18: 1.5 s longer (was 2.7 s, 90 cards started within 0.6 s): the cards set off over 2.1 s, and
// more of them so the shower stays as thick
const CONFETTI_MS = 4200;
const CONFETTI_PIECES = 150;
const CONFETTI_RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

/** A burst of the player's real cards over the whole page and the window (win.css .win_confetti):
 *  intense and short (owner 2026-10-17). Laid out from the index (no Math.random: the e2e seeds it). */
function WinConfetti({ deck }: { deck: string }) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(() => setOn(false), CONFETTI_MS);
    return () => window.clearTimeout(id);
  }, []);
  if (!on) return null;
  const frac = (n: number) => n - Math.floor(n);
  const pieces = Array.from({ length: CONFETTI_PIECES }, (_, i) => {
    const a = frac(Math.sin(i * 12.9898) * 43758.5453);
    const b = frac(Math.sin(i * 78.233) * 12543.1234);
    const c = frac(Math.sin(i * 39.425) * 24634.6345);
    const style = {
      '--x': `${(i / CONFETTI_PIECES) * 100 + (a - 0.5) * 4}vw`,
      '--dx': `${(b - 0.5) * 220}px`,
      '--r': `${(c - 0.5) * 900}deg`,
      '--ry': `${360 + Math.round(a * 4) * 180}deg`,
      '--s': `${0.7 + b * 0.6}`,
      '--d': `${Math.round(c * 2100)}ms`,
      '--t': `${1400 + Math.round(a * 600)}ms`,
      backgroundImage: `url("${faceSrc(deck, `${CONFETTI_RANKS[i % 13]}${'cdhs'[i % 4]}`)}")`,
    } as React.CSSProperties;
    return <i key={i} style={style} />;
  });
  return createPortal(
    <div className="win_confetti" aria-hidden="true">
      {pieces}
    </div>,
    document.body,
  );
}

const pad = (n: number) => String(n).padStart(2, '0');
/** Legacy `toTime()`: h:mm:ss. */
export const formatWinTime = (s: number) =>
  `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
const BEST_KEY = (scoring: string, game: string) => `solitaire_storage::best-score-${scoring}-${game}`;
// 30 x 20 ms = 0.6 s per row: 2.4 s for the four counted rows (standard scoring), 3 s with a timed
// bonus; legacy was 50 x 20 ms = 1 s per row (the prototype's brief caps the celebration at ~3 s).
const COUNT_UP_STEPS = 30;
const COUNT_UP_TICK_MS = 20;
const easeInOut = (j: number) => (j < 0.5 ? 2 * j * j : -1 + (4 - 2 * j) * j);

function readBest(key: string): number {
  try {
    return Number(localStorage.getItem(key) ?? 0) || 0;
  } catch {
    return 0;
  }
}

/** "Play with people" beside the best score: a real person's name when someone waits, else the question. */
function WinOnlineLine({ game }: { game: string }) {
  const [waiter, setWaiter] = useState<Waiter | null>(null);
  useEffect(() => {
    let live = true;
    void fetchWaiting().then((list) => {
      const w = waiterFor(list, game);
      if (!live || !w) return;
      reportWinLine('named');
      setWaiter(w);
    });
    return () => {
      live = false;
    };
  }, [game]);
  const play = () => {
    reportWinLine('click');
    if (waiter) reportWinLine('nclick');
    mpStore().toLobbyFromWin(game, waiter?.id); // a full reload, as New game: the ads refresh (owner's rule)
  };
  return <WinOnlineStrip key={waiter?.id ?? 'ask'} waiter={waiter} onPlay={play} />;
}

function WinOnlineStrip({ waiter, onPlay }: { waiter: Waiter | null; onPlay: () => void }) {
  // the row beside the best score holds the short text only in many languages: the longer part goes when the strip would
  // come within 48 px of the score (it still counts up, and five digits are wider). Layout widths, not the screen's: the
  // window and the score's row are still moving while they open.
  const strip = useRef<HTMLButtonElement>(null);
  const [short, setShort] = useState(false);
  useLayoutEffect(() => {
    const fit = () => {
      const el = strip.current;
      const win = el?.closest<HTMLElement>('.lbx_win');
      const name = win?.querySelector<HTMLElement>('td.td_best_score.name');
      const value = win?.querySelector<HTMLElement>('#win_best_score');
      if (!el || !win || !name || !value || window.innerWidth < 760) return;
      const room = (win.offsetWidth - name.offsetWidth - value.offsetWidth) / 2 - 12;
      // a long name never leaves room for the game beside the score (the strip stops at 270 px)
      if (el.offsetWidth + 48 > room || (waiter && waiter.name.length > 12)) setShort(true);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [waiter]);
  if (waiter) {
    const label = t(`win.${waiter.variant}`);
    return (
      <button ref={strip} type="button" className="win_online win_online_named" onClick={onPlay}>
        <span className="win_online_who">
          <WithFlag text={short ? t('win.online-waits-short', { name: waiter.name }) : t('win.online-waits', { name: waiter.name, game: label })} name={waiter.name} flag={waiter.flag} />
        </span>{' '}
        <b>{t('lobby.room-join')}</b>
      </button>
    );
  }
  return (
    <button ref={strip} type="button" className="win_online" onClick={onPlay}>
      {short ? null : `${t('win.online-ask')} `}
      <b>{t('win.online-play')}</b>
    </button>
  );
}

export function WinWindow({ instance, onRetry }: { instance: WindowInstance; onRetry: () => void }) {
  const data = instance.props as unknown as WinData;
  const preview = data.preview === true;
  const bonus = winBonus(data.scoring, data.time);
  const shown = preview ? data.score : data.score + bonus;
  // a low screen: the window drops its extras step by step until it fits (useFitWindow.ts, win.css data-fit-*)
  useFitWindow('.lbx_window.lbx_win', 9, preview);
  const bestKey = BEST_KEY(data.scoring, data.game);
  const [best] = useState(() => (preview ? 0 : Math.max(readBest(bestKey), shown)));
  const [values, setValues] = useState({ moves: 0, time: 0, bonus: 0, score: 0, best_score: 0 });
  const [active, setActive] = useState<Record<string, boolean>>({});
  // Prototype hooks for the Win window's CSS (mix/win.css): `win_done` on a cell once its count has
  // ended (the row being counted / the score pop need no :has()), `data-record` on .win_cont when
  // the score ties or beats the stored best (legacy `>=`: a tie is a record).
  const [done, setDone] = useState<Record<string, boolean>>({});
  const started = useRef(false);
  const { skin } = uiStore().getState().options;
  // Owner's ask (2026-10-15): a second ad slot at the top of the window when the screen has room
  // (the legacy code had the same idea, disabled: `adsOffTop = ... getHeight() < 680`).
  const [topAd, setTopAd] = useState(() => matchMedia(TOP_AD_MEDIA).matches);
  useEffect(() => {
    const mq = matchMedia(TOP_AD_MEDIA);
    const on = () => setTopAd(mq.matches);
    return onMediaChange(mq, on);
  }, []);

  useEffect(() => {
    // The count-up restarts whenever the effect runs (React StrictMode runs it twice in dev and the
    // first run's cleanup clears the timers); the storage write and the POST happen once.
    const first = !started.current;
    started.current = true;
    // Best score: stored WITH the bonus (legacy statsWin.open merged {score: score + bonus} into its
    // data before storing it; the catalog's "stored raw" note was wrong), compared the same way.
    if (first && !preview && !data.demo && shown >= readBest(bestKey)) {
      try {
        localStorage.setItem(bestKey, String(shown));
      } catch {
        /* no storage */
      }
    }
    const targets: Array<[keyof typeof values, number]> = [
      ['moves', data.moves],
      ['time', data.time],
      ['bonus', bonus],
      ['score', shown],
      ['best_score', best],
    ];
    const order = preview ? targets.filter(([k]) => k !== 'best_score') : targets;
    const mobile = document.body.id === 'mobile';
    let cancelled = false;
    const timers: number[] = [];
    const run = (i: number) => {
      if (cancelled || i >= order.length) return;
      const [key, target] = order[i]!;
      setActive((a) => ({ ...a, [key]: true }));
      if (mobile || target === 0) {
        setValues((v) => ({ ...v, [key]: target }));
        setDone((d) => ({ ...d, [key]: true }));
        run(i + 1);
        return;
      }
      let j = 0;
      const id = window.setInterval(() => {
        j += 1 / COUNT_UP_STEPS;
        if (j >= 1) {
          window.clearInterval(id);
          setValues((v) => ({ ...v, [key]: target }));
          setDone((d) => ({ ...d, [key]: true }));
          run(i + 1);
        } else setValues((v) => ({ ...v, [key]: Math.floor(easeInOut(j) * target) }));
      }, COUNT_UP_TICK_MS);
      timers.push(id);
    };
    run(0);
    if (first && !preview && !data.demo && env.appTarget !== 'offline') {
      const prevDeal: 0 | 1 = data.prevDeal ? 1 : 0;
      void post('stats/save-win.html', {
        game_type: data.game,
        scoring_type: data.scoring,
        time: data.time,
        score: shown,
        moves: data.moves,
        prev_deal: prevDeal,
        shuffle_pattern: data.pattern.join(','),
        hash: winHash(data.game, data.scoring, data.time, shown, data.moves, prevDeal),
      });
    }
    return () => {
      cancelled = true;
      for (const id of timers) window.clearInterval(id);
    };
  }, [bestKey, best, bonus, data, preview, shown]);

  const newGame = () => {
    // A full reload (it refreshes the ads, owner decision). Assigning a URL that differs only by
    // its hash does not reload, so the hash is set first and the page is reloaded explicitly.
    const target = `${env.fullAddr}#${data.game}`;
    mpStore().holdForReload(); // a waiting online room stays through the reload
    if (location.href.split('#')[0] === target.split('#')[0]) {
      location.hash = data.game;
      location.reload();
    } else {
      location.href = target;
    }
  };
  // "Play with people" (the half split of ui/winLine.ts): not for a player who already waits or sits at a table
  const mp = useMultiplayerState();
  const showLine = !preview && !data.demo && winLineGroup() === 'a' && typeof WebSocket === 'function' && !mp.table && !mp.gameStarted;
  const cls = (key: string, extra: string) =>
    `${extra} ${active[key] ? 'win_active' : 'win_inactive'}${done[key] ? ' win_done' : ''}`;
  const record = !preview && best === shown;

  return (
    <WindowFrame
      name="win"
      header={t('winHeader')}
      closable={false}
      closing={instance.closing}
      className={adsEnabled() ? '' : 'no-ads'}
    >
      <div className="tab_contents">
        {topAd ? <AdSlot slot={2} top /> : null}
        <WinBackdrop back={skin.cardBackType} />
        {preview ? null : <WinConfetti deck={skin.deckType} />}
        <div className="win_cont" data-record={record ? '' : undefined}>
          {/* the hero: the fan and the medal, the headline and the subline (win.css .win_hero) */}
          <div className="win_hero">
            <WinHero deck={skin.deckType} />
            <div className="win_congrats">{t('win.congrats')}</div>
            <div className="win_subline">{record ? t('win.newBest') : t('win.wellPlayed')}</div>
          </div>
          <table>
            <tbody>
              <tr>
                <td className="name td_game_type">{t('winGameTypeHead')}</td>
                <td className="value td_game_type">{t(`win.${data.game}`)}</td>
              </tr>
              <tr>
                <td className="name td_score_type">{t('winScoringSystemHead')}</td>
                <td className="value td_score_type">
                  {t(`winScoringSystem${data.scoring === 'timed' ? 'Timed' : 'Standard'}`)}
                </td>
              </tr>
              <tr>
                <td className={cls('moves', 'name td_moves')}>{t('winMovesHead')}</td>
                <td id="win_moves" className={cls('moves', 'value td_moves')}>
                  {values.moves}
                </td>
              </tr>
              <tr>
                <td className={cls('time', 'name td_time')}>{t('winTimeHead')}</td>
                <td id="win_time" className={cls('time', 'value td_time')}>
                  {formatWinTime(values.time)}
                </td>
              </tr>
              <tr>
                <td className={cls('bonus', 'name td_bonus')}>{t('winBonusHead')}</td>
                <td id="win_bonus" className={cls('bonus', 'value td_bonus')}>
                  {active.bonus ? values.bonus : '-'}
                </td>
              </tr>
              <tr>
                <td className={cls('score', 'name td_score')}>{t('winScoresHead')}</td>
                <td id="win_score" className={cls('score', 'value td_score')}>
                  {values.score}
                </td>
              </tr>
              {/* no best score in the /my-score/ preview: the row is left out, not hidden (legacy hid it with
                  visibility, and the empty line left a big gap above "by <name>", owner 2026-10-18); the
                  "by" line takes its place and its plain-line look (win.css tr:nth-child(7)) */}
              {preview ? null : (
                <tr>
                  <td className={cls('best_score', 'name td_best_score')}>{t('winBestScoresHead')}</td>
                  <td id="win_best_score" className={cls('best_score', 'value td_best_score')}>
                    {values.best_score}
                  </td>
                </tr>
              )}
              {preview ? (
                <tr>
                  <td colSpan={2} className="name td_player">
                    by {data.winnerName ?? '???'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {showLine ? <WinOnlineLine game={data.game} /> : null}
          <AdSlot slot={2} />
        </div>
        <div className="win_buttons">
          {/* the /my-score/ preview: the shared win's deal is already on the board (app-entry.tsx), so its
              one button only closes the window, no reload (owner 2026-10-23: "New game" reloaded the page) */}
          {preview ? (
            <input className="play_deal" type="button" value={t('win.playDeal')} onClick={() => uiStore().closeWindow('win')} />
          ) : (
            <>
              <input className="new_game" type="button" value={t('winNewGameButton')} onClick={newGame} />
              <input
                className="redeal_game"
                type="button"
                value={t('winRedealButton')}
                onClick={() => {
                  uiStore().closeWindow('win');
                  onRetry();
                }}
              />
            </>
          )}
        </div>
      </div>
    </WindowFrame>
  );
}
